import idleController from '@helpers/idleController';
import {getAppWindow, onAppWindowChange} from '@helpers/appWindow';
import rootScope from '@lib/rootScope';
import AccountController from '@lib/accounts/accountController';
import {getCurrentAccount} from '@lib/accounts/getCurrentAccount';
import {onCorpForegroundResume} from '@lib/corp/monitor';

/**
 * When the tab was minimized / backgrounded, the MTProto socket may die while the
 * UI still thinks it is Connected — connectionStatus only forceGetDifference()s on
 * Connecting→Connected. Resume from idle/visibility and explicitly reconnect + pull
 * difference so chat history (and 777000 codes) catch up.
 *
 * ⚠️ 未登录（扫码/手机号登录页）时绝不调用需授权的 MTProto（forceGetDifference 等）。
 * 否则 401 → apiManager 全局登出 → location.reload，造成员工/审查端登录页反复刷新。
 */

const MIN_CATCHUP_INTERVAL_MS = 2000;
let lastCatchupAt = 0;
let started = false;
let boundDoc: Document | undefined;
let onVisibility: (() => void) | undefined;

async function currentUserId(): Promise<string | undefined> {
  try {
    const acc = await AccountController.get(getCurrentAccount());
    return acc && acc.userId ? '' + acc.userId : undefined;
  } catch(err) {
    return undefined;
  }
}

async function catchUpFromBackground(reason: string) {
  // 登录门槛：未登录时直接跳过，避免扫码页 401 刷新循环（员工/审查共用）。
  const userId = await currentUserId();
  if(!userId) return;

  const now = Date.now();
  if(now - lastCatchupAt < MIN_CATCHUP_INTERVAL_MS) {
    return;
  }
  lastCatchupAt = now;

  const managers = rootScope.managers;
  if(!managers?.apiUpdatesManager || !managers?.networkerFactory) {
    return;
  }

  try {
    console.log('[sync] foreground catch-up:', reason);
    // Multi-account: clear akStopped if a non-SharedWorker tab had stopAll()'d.
    if(managers.all?.networkerFactory) {
      await managers.all.networkerFactory.startAll();
    } else {
      await managers.networkerFactory.startAll();
    }
    await managers.networkerFactory.forceReconnect();
    await managers.apiUpdatesManager.forceGetDifference();
  } catch(err) {
    console.warn('[sync] foreground catch-up failed', err);
  }

  onCorpForegroundResume();
}

function bindVisibility(win: Window) {
  if(boundDoc && onVisibility) {
    boundDoc.removeEventListener('visibilitychange', onVisibility);
  }

  const doc = win.document;
  onVisibility = () => {
    if(!doc.hidden) {
      catchUpFromBackground('visibilitychange');
    }
  };
  doc.addEventListener('visibilitychange', onVisibility);
  boundDoc = doc;
}

export default function initForegroundSyncCatchup() {
  if(started || typeof window === 'undefined') return;
  started = true;

  idleController.addEventListener('change', (idle) => {
    if(!idle) {
      catchUpFromBackground('idle→active');
    }
  });

  bindVisibility(getAppWindow());
  onAppWindowChange((win) => bindVisibility(win));

  window.addEventListener('pageshow', (e) => {
    if(e.persisted) {
      catchUpFromBackground('pageshow-bfcache');
    }
  });
}
